import { gameFilterValueFromConceptKey } from "../../game-filter-param.js";
import { gameColorFromConceptKey, gameDisplayLabel, gameShortLabel } from "../../lib/gamePalette.js";
import type { EngagementData, OpenRawEventsHandler } from "../../types.js";
import { ChartCard } from "../ChartCard.js";
import { PieChart } from "../charts/PieChart.js";

const GAME_MINUTES_CRITERIA = `Source: device_events where event_name = 'screen_exited', **screen_name** = **game**, same dashboard filters.

Each slice: sum of **duration_ms** on those rows (shell game-screen dwell), grouped by canonical playable key (\`/games/{slug}\`) using session-journey attribution when the exit row has no route/name. Colours match session journey / game palette.

Not the same as **Minutes per Start** (play minutes ÷ **game_started** count per key).

Slice click: Raw Events → **screen_exited** with **screen_name** = game and game_url filter for that slice.`;

interface GameMinutesShareCardProps {
  byGameMinutesPct: EngagementData["byGameMinutesPct"];
  totalPlayMinutes: number;
  onOpenRawEvents: OpenRawEventsHandler;
  showCriteria?: boolean;
}

export function GameMinutesShareCard({
  byGameMinutesPct,
  totalPlayMinutes,
  onOpenRawEvents,
  showCriteria = true,
}: GameMinutesShareCardProps) {
  return (
    <ChartCard
      title="Game Minutes Share"
      titleRight={
        <span className="shrink-0 text-[12px] font-medium tabular-nums text-text-secondary">
          Total {totalPlayMinutes.toFixed(1)} min
        </span>
      }
      criteria={showCriteria ? GAME_MINUTES_CRITERIA : undefined}
      note="Share of play time by game — use Minutes per Start for avg length per launch."
    >
      <PieChart
        slices={byGameMinutesPct.map((x) => ({
          label: gameDisplayLabel(x.label),
          pieLabel: gameShortLabel(x.label),
          value: x.minutes,
          color: gameColorFromConceptKey(x.label),
        }))}
        formatValue={(v) => `${v.toFixed(1)} min`}
        sliceTooltip={(slice, i) => {
          const row = byGameMinutesPct[i];
          const pct = row ? Math.round(row.value * 100) : 0;
          return `${slice.label}: ${slice.value.toFixed(1)} min (${pct}% of play time) — click: Raw Events → screen_exited (game screen). Middle-click / Ctrl/Cmd+click: new tab.`;
        }}
        onSliceClick={(slice, i, e) => {
          const row = byGameMinutesPct[i];
          if (!row) return;
          onOpenRawEvents(
            {
              eventName: "screen_exited",
              propertiesContains: `"screen_name":"game"`,
              gameUrl: gameFilterValueFromConceptKey(row.label),
            },
            e,
          );
        }}
      />
    </ChartCard>
  );
}
